import { useEffect, useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, Icon } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { SUPPORTED_CURRENCY_CODES } from '@/utils/currency';
import type { HospitalConfig } from '@/services/api/systemSettingsApi';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';
import { PickerModal, TextEditor } from './editors';
import { SettingsUnreadable } from './Unreadable';
import { countryOptions, isCountry } from '@/lib/phone';
import { useFacility, useSaveFacility } from './useSettings';

/**
 * Clinic manager · Settings ▸ Facility — `g_set_fac.dc.html`.
 *
 * "AS BUILT, + THE CURRENCY" is the board's own subtitle, and it is a note
 * about scope: every field here already existed on `hospital.config` (contact,
 * locale, licence) — the one the redesign adds is the display currency, which
 * is why it is the single accent card on the screen and why the accent note at
 * the bottom exists to say where its FORMAT lives instead.
 *
 * NOTHING SAVES UNTIL SAVE. Each card opens its own editor, the edits collect
 * in one draft, the "⚠ unsaved" chip counts them, and the top bar's single
 * primary writes the whole config in one PUT — the shape the endpoint takes.
 * That chip is live: it is absent on a clean form, because a warning that is
 * always on is not a warning.
 *
 * WHY THE ARROWS ARE REAL. Language, currency and the licence's issuer are
 * drawn with "▾" on the board, so each of them opens a real picker (BRIEF
 * §4.8) — and "Valid until" opens a real date field, not a calendar glyph.
 */

type EditKey = 'phone' | 'email' | 'website' | 'license' | 'validUntil' | 'issuer' | null;
type PickKey = 'locale' | 'currency' | 'country' | 'issuer' | null;

/** The authorities a licence is issued by in the markets MAIE ships to. */
const ISSUERS = [
  'Ministry of Health',
  'Ministry of Health and Population',
  'Egyptian Medical Syndicate',
  'Saudi Health Council',
  'Dubai Health Authority',
  'Department of Health — Abu Dhabi',
  'Ministry of Public Health',
];

/** "EGP — Egyptian pound", from the browser's own currency names. */
function currencyLabel(code: string): string {
  let name = code;
  try {
    name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) ?? code;
  } catch { /* an engine without DisplayNames prints the code alone. */ }
  /* The canvas writes "Egyptian pound": the UNIT is a common noun, everything
     in front of it is a proper one. So only the last word comes down —
     lower-casing the lot turned "United Arab Emirates Dirham" into "United arab
     emirates dirham" (round-2 MINOR). An all-caps word is left alone, so
     "CFA Franc BCEAO" keeps its acronyms. */
  const words = name.split(' ');
  const last = words.length - 1;
  const sentence = words
    .map((w, i) => (i === last && w !== w.toUpperCase() ? w.toLowerCase() : w))
    .join(' ');
  return sentence === code ? code : `${code} — ${sentence}`;
}

const NOT_SET = 'Not set';

export function ManagerFacilityPage() {
  const go = useNavigate();
  const { facility, isLoading, error, refetch } = useFacility();
  const save = useSaveFacility();
  const [draft, setDraft] = useState<HospitalConfig | null>(null);
  const [editing, setEditing] = useState<EditKey>(null);
  const [picking, setPicking] = useState<PickKey>(null);
  const [query, setQuery] = useState('');

  usePageMeta({ title: 'Facility · System Settings' });

  /* THE DRAFT IS SEEDED ONCE, FROM THE SERVER. Re-seeding on every render of
     `facility` would throw away the manager's edits the moment react-query
     refetched in the background. */
  useEffect(() => {
    if (facility && !draft) setDraft(facility);
  }, [facility, draft]);

  const value = draft ?? facility;
  const dirty = !!draft && !!facility && JSON.stringify(draft) !== JSON.stringify(facility);

  /* THE READ EITHER RESOLVED OR IT DID NOT. `GET /clinic-settings/facility`
     always answers with a config when it answers at all — `getHospitalConfig`
     synthesises one for a clinic that has never opened Settings — so a settled
     read with no value means refused (403 for every role but Owner and Clinic
     Manager) or failed. Either way there is no record to edit, and §4.10 says
     this must not render as an empty form. */
  const reading = isLoading && !value;
  const unreadable = !reading && !value;

  const patch = (fn: (next: HospitalConfig) => void) => {
    setDraft((prev) => {
      const base = prev ?? facility;
      if (!base) return prev;
      const next = JSON.parse(JSON.stringify(base)) as HospitalConfig;
      fn(next);
      return next;
    });
  };

  const currency = value?.billingCurrency || 'EGP';
  const locale = value?.defaultLocale ?? 'en';
  const phoneCountry = isCountry(value?.defaultCountry) ? value.defaultCountry : 'EG';
  const countries = useMemo(() => countryOptions('en', phoneCountry), [phoneCountry]);
  const countryLabel = (code: string) => {
    const c = countries.find((x) => x.code === code);
    return c ? `${c.flag} ${c.name} (+${c.dial})` : code;
  };

  const fields = useMemo(() => ([
    { key: 'phone', label: 'Phone', text: value?.contact?.phone },
    { key: 'email', label: 'Email', text: value?.contact?.email },
    { key: 'website', label: 'Website', text: value?.contact?.website },
    { key: 'language', label: 'Default language', text: locale === 'ar' ? 'العربية' : 'English' },
    { key: 'currency', label: 'Display currency', text: currencyLabel(currency) },
    { key: 'country', label: 'Phone country', text: countryLabel(phoneCountry) },
    { key: 'license', label: 'License №', text: value?.license?.number },
    { key: 'issuer', label: 'Issued by', text: value?.license?.issuedBy },
    { key: 'valid', label: 'Valid until', text: value?.license?.validUntil },
  ]), [value, locale, currency, phoneCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = query.trim().toLowerCase();
  const shows = (key: string) => {
    if (!q) return true;
    const f = fields.find((x) => x.key === key);
    return `${f?.label ?? ''} ${f?.text ?? ''}`.toLowerCase().includes(q);
  };
  const anyShown = fields.some((f) => shows(f.key));

  const crumb = usePhoneCrumb('Settings', 'Facility');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Facility',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Facility
        </>
      ),
      /* NOTHING TO SEARCH AND NOTHING TO SAVE when the record never arrived:
         a field that can never match and a primary that cannot write are both
         §4.8 dead controls, so neither is published in that state. */
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="facility" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: draft
        ? {
          label: save.isPending ? 'Saving…' : 'Save',
          onClick: () => save.mutate(draft),
        }
        : undefined,
    },
    [query, dirty, save.isPending, !!draft, unreadable],
  );

  const editorValue =
    editing === 'phone' ? (value?.contact?.phone ?? '')
      : editing === 'email' ? (value?.contact?.email ?? '')
        : editing === 'website' ? (value?.contact?.website ?? '')
          : editing === 'license' ? (value?.license?.number ?? '')
            : editing === 'validUntil' ? (value?.license?.validUntil ?? '')
              : editing === 'issuer' ? (value?.license?.issuedBy ?? '')
                : '';

  const editorTitle =
    editing === 'phone' ? 'Phone'
      : editing === 'email' ? 'Email'
        : editing === 'website' ? 'Website'
          : editing === 'license' ? 'License №'
            : editing === 'issuer' ? 'Issued by'
              : 'Valid until';

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s facility…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="facility" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {value ? (
          <>
            {shows('phone') || shows('email') || shows('website') ? (
              <>
                <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>
                  Contact information — how patients and partners reach the facility
                </p>
                <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {shows('phone') ? (
                    <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('phone')}>
                      <p className="t-ovl mut">Phone</p>
                      <p className="t-strong mt4">
                        {value?.contact?.phone || <span className="mut">{NOT_SET}</span>} <Icon name="pencil" size="sm" />
                      </p>
                    </Card>
                  ) : null}
                  {shows('email') ? (
                    <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('email')}>
                      <p className="t-ovl mut">Email</p>
                      <p className="t-strong mt4">
                        {value?.contact?.email || <span className="mut">{NOT_SET}</span>} <Icon name="pencil" size="sm" />
                      </p>
                    </Card>
                  ) : null}
                  {shows('website') ? (
                    <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('website')}>
                      <p className="t-ovl mut">Website</p>
                      <p className="t-strong mt4">
                        {value?.contact?.website || <span className="mut">{NOT_SET}</span>} <Icon name="pencil" size="sm" />
                      </p>
                    </Card>
                  ) : null}
                </div>
              </>
            ) : null}

            {shows('language') || shows('currency') || shows('country') || shows('license') || shows('issuer') || shows('valid') ? (
              <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Location &amp; license</p>
            ) : null}

            {shows('language') || shows('currency') || shows('country') ? (
              <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {shows('language') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setPicking('locale')}>
                    <p className="t-ovl mut">Default language</p>
                    <p className="t-strong mt4">{locale === 'ar' ? 'العربية' : 'English'} ▾</p>
                  </Card>
                ) : null}
                {shows('currency') ? (
                  <Card kind="accent" style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setPicking('currency')}>
                    <p className="t-ovl mut">Display currency</p>
                    <p className="t-strong mt4">{currencyLabel(currency)} ▾</p>
                  </Card>
                ) : null}
                {shows('country') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setPicking('country')}>
                    <p className="t-ovl mut">Phone country</p>
                    <p className="t-strong mt4">{countryLabel(phoneCountry)} ▾</p>
                    <p className="t-cap mut mt4">a number typed without its code belongs here</p>
                  </Card>
                ) : null}
              </div>
            ) : null}

            {shows('license') || shows('issuer') || shows('valid') ? (
              <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {shows('license') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('license')}>
                    <p className="t-ovl mut">License №</p>
                    <p className="t-strong mt4">
                      {value?.license?.number || <span className="mut">{NOT_SET}</span>} <Icon name="pencil" size="sm" />
                    </p>
                  </Card>
                ) : null}
                {shows('issuer') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setPicking('issuer')}>
                    <p className="t-ovl mut">Issued by</p>
                    <p className="t-strong mt4">
                      {value?.license?.issuedBy || <span className="mut">{NOT_SET}</span>} ▾
                    </p>
                  </Card>
                ) : null}
                {shows('valid') ? (
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('validUntil')}>
                    <p className="t-ovl mut">Valid until</p>
                    <p className="t-strong mt4">
                      {value?.license?.validUntil || <span className="mut">Select date</span>} <Icon name="calendar" size="sm" />
                    </p>
                  </Card>
                ) : null}
              </div>
            ) : null}

            {!anyShown ? (
              <p className="t-sec mut" style={{ marginTop: 18 }}>
                No field on this screen matches that. Clear the search to see the facility again.
              </p>
            ) : null}

            <Card kind="accent">
              <p className="t-sec brand">
                The currency is chosen here; its FORMAT — where the symbol sits and how many decimals
                print — lives in Money, because that is what every bill, statement and payslip reads.
                {' '}
                {/* The board's own closing link, kept word for word, with the
                    honesty mark BESIDE it rather than in place of it — the pattern
                    the thirteen doors on `g_set` already use (§4.8): the label
                    stays, a separate caption says it is not built, and neither is
                    a control, so nothing here pretends to navigate. */}
                <button type="button" className="w6 brand"
                  style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                  onClick={() => go('/clinic-manager/settings/money')}>
                  Open Money ›
                </button>
              </p>
            </Card>

            {save.isError ? (
              <p className="t-sec red" style={{ marginTop: 10 }}>
                That did not save. Nothing was changed on the server — try again, or reload to see
                what is stored.
              </p>
            ) : null}
            {save.isSuccess && !dirty ? (
              <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p>
            ) : null}
          </>
        ) : null}
      </div>

      <TextEditor
        open={editing !== null}
        title={editorTitle}
        sub={editing === 'validUntil' ? 'the day this licence stops being valid' : undefined}
        value={editorValue}
        type={editing === 'validUntil' ? 'date'
          : editing === 'email' ? 'email'
            : editing === 'website' ? 'url'
              : editing === 'phone' ? 'tel' : 'text'}
        onClose={() => setEditing(null)}
        onDone={(next) => {
          const key = editing;
          patch((c) => {
            if (key === 'phone') c.contact.phone = next;
            if (key === 'email') c.contact.email = next;
            if (key === 'website') c.contact.website = next;
            if (key === 'license') c.license.number = next;
            if (key === 'validUntil') c.license.validUntil = next;
            if (key === 'issuer') c.license.issuedBy = next;
          });
        }}
      />

      <PickerModal
        open={picking === 'locale'}
        title="Default language"
        sub="the language this clinic’s screens, documents and messages open in"
        value={locale}
        options={[
          { value: 'en', label: 'English' },
          { value: 'ar', label: 'العربية', note: 'Arabic' },
        ]}
        onClose={() => setPicking(null)}
        onPick={(next) => patch((c) => { c.defaultLocale = next as 'en' | 'ar'; })}
      />

      <PickerModal
        open={picking === 'currency'}
        title="Display currency"
        sub="every amount in the EMR is shown in this currency"
        value={currency}
        options={SUPPORTED_CURRENCY_CODES.map((code) => ({ value: code, label: currencyLabel(code) }))}
        onClose={() => setPicking(null)}
        onPick={(next) => patch((c) => { c.billingCurrency = next; })}
      />

      <PickerModal
        open={picking === 'country'}
        title="Phone country"
        sub="a mobile typed the local way (0100 …) belongs to this country; anyone else’s is typed with its code or picked from the flag"
        value={phoneCountry}
        searchable
        options={countries.map((c) => ({
          value: c.code,
          label: `${c.flag} ${c.name}`,
          note: `+${c.dial}`,
          search: `${c.name} ${c.code} +${c.dial} ${c.dial}`,
        }))}
        onClose={() => setPicking(null)}
        onPick={(next) => patch((c) => { c.defaultCountry = next; })}
      />

      <PickerModal
        open={picking === 'issuer'}
        title="Issued by"
        sub="the authority that issued this facility’s licence"
        value={value?.license?.issuedBy ?? ''}
        options={ISSUERS.map((name) => ({ value: name, label: name }))}
        onClose={() => setPicking(null)}
        onPick={(next) => patch((c) => { c.license.issuedBy = next; })}
        extra={(
          <button type="button" className="mi"
            onClick={() => { setPicking(null); setEditing('issuer'); }}>
            <span className="grow mut">Another authority — type its name</span>
          </button>
        )}
      />
    </div>
  );
}
